import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Dialog, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { SfLink } from "./ui";
import {
    toISODate,
    useAccountContacts,
    useSalesforceLink,
    useConsultantVisits,
    useWeeklyVisitSummary,
    useBookVisit,
    dayExceptionFor,
    isPastDay,
    sameDay,
} from "./data";
import type { SfVisit, VisitConsultantT } from "./data";

/** Book visit. The server refuses rather than throws, so `booked: false` is a normal answer. */

export interface BookTarget {
    visit: SfVisit;
    /** Pre-selects the date when opened from a specific day in the planner. */
    day?: Date;
}

/** 'On Site' and 'Remote' are the exact picklist strings; anything else is rejected. */
const MODES = ["On Site", "Remote"] as const;
/** Free text in Salesforce, but only these two values are ever used in practice. */
const LENGTHS = ["Full day", "Half day"] as const;

// 95.3% of real bookings land on the hour or half hour, so nothing finer is offered.
const TIMES = Array.from({ length: 25 }, (_, i) => {
    const mins = 7 * 60 + i * 30;
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
});

/** " 10:30-13:30" when the booked window is known, otherwise nothing. */
const fmtRange = (v: { visitStartDate?: string | null; visitEndDate?: string | null }) => {
    const t = (iso?: string | null) => {
        if (!iso) return null;
        const d = new Date(iso);
        return isNaN(d.getTime()) ? null : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    };
    const a = t(v.visitStartDate);
    const b = t(v.visitEndDate);
    return a ? ` ${a}${b ? `–${b}` : ""}` : "";
};

const addHours = (hhmm: string, hours: number) => {
    const [h, m] = hhmm.split(":").map(Number);
    const total = Math.min(h * 60 + m + hours * 60, 23 * 60 + 30);
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const inputSx = {
    border: `1px solid ${T.border}`,
    borderRadius: "8px",
    padding: "7px 10px",
    fontSize: "13px",
    fontFamily: T.font,
    color: T.ink2,
    background: T.card,
    outline: "none",
    width: "100%",
    "&:focus": { borderColor: T.purple },
};

const Label = ({ children, hint }: { children: string; hint?: string }) => (
    <Typography
        title={hint}
        sx={{
            fontSize: "10.5px",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: T.muted2,
            mb: "4px",
            cursor: hint ? "help" : "default",
        }}
    >
        {children}
    </Typography>
);

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <Box sx={{ minWidth: 0 }}>
        <Label hint={hint}>{label}</Label>
        {children}
    </Box>
);

const BookVisitModal = ({ target, onClose }: { target: BookTarget | null; onClose: () => void }) => {
    const visit = target?.visit;
    const sfLink = useSalesforceLink();
    const contacts = useAccountContacts(visit?.accountId, !!target);
    const book = useBookVisit();

    const [date, setDate] = useState("");
    const [start, setStart] = useState("09:00");
    const [end, setEnd] = useState("16:00");
    const [mode, setMode] = useState<string>("On Site");
    const [length, setLength] = useState<string>("Full day");
    const [contactId, setContactId] = useState("");
    const [notes, setNotes] = useState("");
    const [override, setOverride] = useState(false);
    const firstName = (visit?.allocatedToName || "").split(" ")[0] || "This consultant";

    // Reset on open, or the previous visit's choices leak into the next one.
    useEffect(() => {
        if (!target) return;
        const d = target.day ?? (visit?.dueDate ? new Date(visit.dueDate) : new Date());
        setDate(toISODate(d));
        setStart("09:00");
        setEnd("16:00");
        setMode("On Site");
        setLength("Full day");
        setNotes(visit?.notes || "");
        setOverride(false);
        // Populated only when Salesforce already holds one; blank otherwise.
        setContactId(visit?.visitingContactId || "");
        book.reset();
        // Keyed on target, not the visit id: the id left the old date in place on reopen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    // An account with exactly one contact has no choice to make, so make it.
    useEffect(() => {
        if (!contactId && contacts.data?.length === 1) setContactId(contacts.data[0].id);
    }, [contacts.data, contactId]);

    // A prefilled contact absent from the list leaves the select blank while the form holds an id.
    const orphanContact = !!contactId && !!contacts.data && !contacts.data.some((c) => c.id === contactId);
    useEffect(() => {
        if (orphanContact) setContactId("");
    }, [orphanContact]);

    // Leave and admin live in SQL, so nothing on the Salesforce side can see these clashes.
    const email = visit?.allocatedToEmail ?? null;
    const dayVisits = useConsultantVisits(date, date, email, !!target && !!date && !!email);
    const weekly = useWeeklyVisitSummary(date, !!target && !!date);

    const clashes = useMemo(() => {
        const out: { tone: "red" | "amber"; text: string }[] = [];
        if (!date) return out;
        const chosen = new Date(`${date}T12:00:00`);

        if (isPastDay(chosen)) out.push({ tone: "red", text: "That date is in the past." });
        const dow = chosen.getDay();
        if (dow === 0 || dow === 6)
            out.push({ tone: "amber", text: "That is a weekend — 3.1% of visits are booked on a Friday and almost none at a weekend." });

        // Excludes the visit being booked, so reopening it does not flag itself.
        const already = (dayVisits.data ?? []).filter(
            (v) => v.id !== visit?.id && v.visitStartDate && sameDay(new Date(v.visitStartDate as string), chosen),
        );
        // Two visits a day is pairing; two at the same time is not. Clash on the clock.
        const mins = (hhmm: string) => {
            const [h, m] = hhmm.split(":").map(Number);
            return h * 60 + m;
        };
        const atMins = (iso?: string | null) => {
            if (!iso) return null;
            const d = new Date(iso);
            return isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
        };
        const from = mins(start);
        const to = mins(end);
        // Overlap when each starts before the other ends; an unknown end counts as an hour.
        const overlapping = already.filter((v) => {
            const s = atMins(v.visitStartDate as string);
            if (s == null) return false;
            const e = atMins(v.visitEndDate as string) ?? s + 60;
            return from < e && to > s;
        });

        if (overlapping.length)
            out.push({
                tone: "red",
                text: `${firstName} is already booked over those hours: ${overlapping
                    .map((v) => `${v.accountName || "unnamed account"}${fmtRange(v)}`)
                    .join(", ")}.`,
            });

        const alsoThatDay = already.filter((v) => !overlapping.includes(v));
        if (alsoThatDay.length)
            out.push({
                tone: "amber",
                text: `${firstName} also has ${alsoThatDay.length} visit${alsoThatDay.length === 1 ? "" : "s"} that day: ${alsoThatDay
                    .map((v) => `${v.accountName || "unnamed account"}${fmtRange(v)}`)
                    .join(", ")}.`,
            });

        // Leave, training, admin and sickness come from DiaryException in SQL.
        const row = weekly.data?.regions
            ?.flatMap((r) => r.consultants)
            .find((c: VisitConsultantT) => (c.email || "").toLowerCase() === (email || "").toLowerCase());
        const ex = row ? dayExceptionFor(row, chosen) : null;
        if (ex) {
            const kind = (ex.status || "").toUpperCase();
            const blocking = kind === "H" || kind === "S";
            out.push({
                tone: blocking ? "red" : "amber",
                text: `${firstName} is marked "${ex.comment}" that day.`,
            });
        }
        return out;
    }, [date, start, end, dayVisits.data, weekly.data, email, visit, firstName]);

    const missing = useMemo(() => {
        const m: string[] = [];
        if (!date) m.push("a date");
        if (!start || !end) m.push("a start and end time");
        else if (end <= start) m.push("an end time after the start");
        if (!contactId) m.push("a visiting contact");
        if (!mode) m.push("on site or remote");
        return m;
    }, [date, start, end, contactId, mode]);

    // From the server's instants, not the form: the only tell for a timezone error.
    const landed = (() => {
        const a = book.data?.visitStart ? new Date(book.data.visitStart) : null;
        const b = book.data?.visitEnd ? new Date(book.data.visitEnd) : null;
        if (!a || isNaN(a.getTime())) return "";
        const t = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
        return `${t(a)}${b && !isNaN(b.getTime()) ? `–${t(b)}` : ""} on ${a.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
        })}`;
    })();

    const blocking = clashes.some((c) => c.tone === "red");
    const booked = !!book.data?.booked;
    const refused = !!book.data && !book.data.booked;
    const canBook = missing.length === 0 && !book.isPending && !booked && (!blocking || override);

    if (!target || !visit) return null;

    const dueDelta = (() => {
        if (!visit.dueDate || !date) return null;
        const days = Math.round((new Date(date).getTime() - new Date(visit.dueDate).getTime()) / 86400000);
        return days > 0 ? days : null;
    })();

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "14px", fontFamily: T.font, background: T.card } }}
        >
            <Box sx={{ padding: "20px 24px 22px", fontFamily: T.font, color: T.ink2 }}>
                <Typography sx={{ fontSize: "17px", fontWeight: 800 }}>Book visit</Typography>
                <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px" }}>
                    {visit.accountName || "Unnamed account"}
                    {visit.subActivity ? ` · ${visit.subActivity}` : ""}
                    {visit.dueDate
                        ? ` · due ${new Date(visit.dueDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                          })}`
                        : ""}
                </Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", mt: "18px" }}>
                    <Field label="Date">
                        <Box
                            component="input"
                            type="date"
                            value={date}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
                            sx={inputSx}
                        />
                    </Field>
                    <Field label="Start">
                        <Box
                            component="select"
                            value={start}
                            onChange={(e: any) => {
                                setStart(e.target.value);
                                setEnd(addHours(e.target.value, 7));
                            }}
                            sx={inputSx}
                        >
                            {TIMES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </Box>
                    </Field>
                    <Field label="End" hint="Defaults to seven hours, the commonest booked duration.">
                        <Box
                            component="select"
                            value={end}
                            onChange={(e: any) => setEnd(e.target.value)}
                            sx={inputSx}
                        >
                            {TIMES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </Box>
                    </Field>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mt: "12px" }}>
                    <Field label="On site or remote">
                        <Box component="select" value={mode} onChange={(e: any) => setMode(e.target.value)} sx={inputSx}>
                            {MODES.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </Box>
                    </Field>
                    <Field label="Visit length" hint="A label only — it is not checked against the times.">
                        <Box
                            component="select"
                            value={length}
                            onChange={(e: any) => setLength(e.target.value)}
                            sx={inputSx}
                        >
                            <option value="">Not set</option>
                            {LENGTHS.map((l) => (
                                <option key={l} value={l}>
                                    {l}
                                </option>
                            ))}
                        </Box>
                    </Field>
                </Box>

                <Box sx={{ mt: "12px" }}>
                    <Field
                        label="Visiting contact"
                        hint="The client-side contact. Almost never set before booking, so it usually has to be chosen here."
                    >
                        {contacts.isLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", py: "6px" }}>
                                <CircularProgress size={14} thickness={5} sx={{ color: T.purple }} />
                                <Typography sx={{ fontSize: "12px", color: T.muted }}>
                                    Loading contacts for this account…
                                </Typography>
                            </Box>
                        ) : contacts.isError ? (
                            <Typography sx={{ fontSize: "12px", color: T.amber }}>
                                Couldn't load contacts for this account.
                            </Typography>
                        ) : (
                            <Box
                                component="select"
                                value={contactId}
                                onChange={(e: any) => setContactId(e.target.value)}
                                sx={inputSx}
                            >
                                <option value="">
                                    {contacts.data?.length ? "Choose a contact…" : "No contacts on this account"}
                                </option>
                                {(contacts.data ?? []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </Box>
                        )}
                    </Field>
                </Box>

                <Box sx={{ mt: "12px" }}>
                    <Field label="Notes">
                        <Box
                            component="textarea"
                            rows={2}
                            value={notes}
                            onChange={(e: any) => setNotes(e.target.value)}
                            placeholder="Optional — anything the consultant needs to know"
                            sx={{ ...inputSx, resize: "vertical" }}
                        />
                    </Field>
                </Box>

                <Box sx={{ mt: "14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    <Typography sx={{ fontSize: "11.5px", color: T.muted }}>
                        <b>Allocated to</b> {visit.allocatedToName || "nobody"} — already set on the visit, so booking
                        does not change it.
                    </Typography>
                    {(dayVisits.isLoading || weekly.isLoading) && (
                        <Typography sx={{ fontSize: "11.5px", color: T.muted2 }}>Checking that day…</Typography>
                    )}
                    {/* The two tones are close in hue, so red also gets a band — the
                        difference decides whether the button works. */}
                    {clashes.map((c, i) => (
                        <Typography
                            key={i}
                            sx={{
                                fontSize: "11.5px",
                                fontWeight: 700,
                                color: c.tone === "red" ? T.red : T.amber,
                                ...(c.tone === "red"
                                    ? {
                                          background: "#FBF1EF",
                                          border: `1px solid ${T.red}`,
                                          borderRadius: "8px",
                                          padding: "7px 10px",
                                      }
                                    : {}),
                            }}
                        >
                            {c.tone === "red" ? "⚠ " : ""}
                            {c.text}
                        </Typography>
                    ))}
                    {dueDelta != null && (
                        <Typography sx={{ fontSize: "11.5px", color: T.amber }}>
                            That is {dueDelta} day{dueDelta === 1 ? "" : "s"} after the due date. Allowed, and normal,
                            but worth a look.
                        </Typography>
                    )}
                    {orphanContact && (
                        <Typography sx={{ fontSize: "11.5px", color: T.amber }}>
                            The contact already on this visit is not in the account's contact list, so it has been
                            cleared — choose one.
                        </Typography>
                    )}
                    {missing.length > 0 && (
                        <Typography sx={{ fontSize: "11.5px", color: T.muted2 }}>
                            Still needs {missing.join(", ")}.
                        </Typography>
                    )}
                    {blocking && !booked && (
                        <Box
                            component="label"
                            sx={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", mt: "2px" }}
                        >
                            <Box
                                component="input"
                                type="checkbox"
                                checked={override}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOverride(e.target.checked)}
                                sx={{ cursor: "pointer" }}
                            />
                            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: T.red }}>
                                Book anyway, despite the warning above
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: "16px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        background: booked ? T.greenTint : T.amberTint,
                        border: `1px solid ${booked ? T.green : T.amberBorder}`,
                    }}
                >
                    <Typography sx={{ fontSize: "11.5px", color: booked ? T.ink2 : T.amber, lineHeight: 1.5 }}>
                        {booked ? (
                            <>
                                <b>Booked{landed ? ` ${landed}` : ""}.</b> {book.data?.message}
                            </>
                        ) : refused ? (
                            <>
                                <b>Not booked.</b> {book.data?.message}
                                {book.data?.visitStart && !book.data?.booked && landed
                                    ? ` It would have been sent as ${landed}.`
                                    : ""}
                            </>
                        ) : book.isError ? (
                            <>
                                <b>Booking failed.</b> It may still have saved — open the visit in Salesforce before
                                trying again.
                            </>
                        ) : (
                            <>
                                <b>Booking writes to Salesforce.</b> It creates an Outlook invite for the client and can
                                raise a Meridian job — read the clash checks first.
                            </>
                        )}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "16px" }}>
                    <SfLink href={sfLink("Visit__c", visit.id)} label="Open this visit in Salesforce" />
                    <Box sx={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                        <Box
                            component="button"
                            disabled={book.isPending}
                            onClick={onClose}
                            sx={{
                                border: `1px solid ${T.border}`,
                                background: T.card,
                                color: T.muted,
                                borderRadius: "8px",
                                padding: "8px 14px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                fontFamily: T.font,
                                cursor: "pointer",
                            }}
                        >
                            Close
                        </Box>
                        <Box
                            component="button"
                            disabled={!canBook}
                            onClick={() =>
                                book.mutate({
                                    id: visit.id,
                                    allocatedToId: visit.allocatedToId || "",
                                    date,
                                    startTime: start,
                                    endTime: end,
                                    visitingContactId: contactId,
                                    visitCategory: mode,
                                    visitLength: length || undefined,
                                    notes: notes || undefined,
                                    consultantEmail: visit.allocatedToEmail,
                                })
                            }
                            title={
                                missing.length
                                    ? `Still needs ${missing.join(", ")}`
                                    : "Writes the booking to Salesforce"
                            }
                            sx={{
                                border: "none",
                                background: canBook ? T.ink : T.border,
                                color: canBook ? "#fff" : T.muted,
                                borderRadius: "8px",
                                padding: "8px 14px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                fontFamily: T.font,
                                cursor: canBook ? "pointer" : "not-allowed",
                                "&:hover": { background: canBook ? T.indigoDeep : T.border },
                            }}
                        >
                            {book.isPending ? "Booking…" : booked ? "Booked" : "Book visit"}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default BookVisitModal;
